import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useBillingUsage } from "@/hooks/useBilling";
import { format, subDays, startOfDay } from "date-fns";
import { supabase } from "@/lib/supabaseClient";
import { useQuery } from '@tanstack/react-query';
import { 
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { Info } from "lucide-react";

interface DailyUsage {
  date: string;
  credits: number;
  cumulativeCredits: number;
}

export function UsageAnalyticsTab() {
  const { data: billingUsage, isLoading: isLoadingUsage } = useBillingUsage();

  // Fetch daily usage data for the current billing period
  const { data: dailyUsage, isLoading: isLoadingDaily } = useQuery({
    queryKey: ['daily-usage-period', billingUsage?.billingPeriodStart],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use billing period dates if available, but ensure proper date range
      let startDate: Date;
      let endDate = new Date();

      // Always show at least 14 days of data for meaningful trend visualization
      const minDaysToShow = 14;
      const fallbackStartDate = subDays(endDate, minDaysToShow);

      if (billingUsage?.billingPeriodStart) {
        const billingStart = new Date(billingUsage.billingPeriodStart);
        
        // If billing period start is in the future, use fallback
        if (billingStart > endDate) {
          console.warn('⚠️ Billing period start is in the future, using fallback date range');
          startDate = fallbackStartDate;
        } 
        // If billing period is too short (less than minimum days), extend it backwards
        else {
          const daysSinceBillingStart = Math.floor((endDate.getTime() - billingStart.getTime()) / (24 * 60 * 60 * 1000));
          if (daysSinceBillingStart < minDaysToShow) {
            console.log(`📅 Billing period is only ${daysSinceBillingStart} days, extending to show ${minDaysToShow} days for better trend visualization`);
            startDate = fallbackStartDate;
          } else {
            startDate = billingStart;
          }
        }
      } else {
        startDate = fallbackStartDate;
      }

      // Ensure startDate is before endDate
      if (startDate > endDate) {
        console.warn('⚠️ Start date after end date, swapping dates');
        [startDate, endDate] = [endDate, startDate];
      }

      console.log('🔍 Usage Chart Debug:', {
        userId: user.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startDateLocal: startDate.toString(),
        endDateLocal: endDate.toString(),
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        billingUsage: {
          billingPeriodStart: billingUsage?.billingPeriodStart,
          billingPeriodEnd: billingUsage?.billingPeriodEnd,
          totalCredits: billingUsage?.totalCredits,
          creditsUsed: billingUsage?.creditsUsed
        }
      });

      // First, let's check if there are ANY call_usage transactions for this user
      const { data: allCallUsage, error: allCallUsageError } = await supabase
        .from('credit_transactions')
        .select('created_at, credits_amount, description')
        .eq('user_id', user.id)
        .eq('transaction_type', 'call_usage')
        .neq('credits_amount', 0)
        .order('created_at', { ascending: false })
        .limit(5);

      console.log('🔎 All Call Usage Transactions Check:', {
        allCallUsage,
        allCallUsageError,
        totalCount: allCallUsage?.length || 0
      });

      // Get credit transactions for the period
      // Updated to handle the new credit tracking system which can create multiple transactions per call
      const { data: transactions, error } = await supabase
        .from('credit_transactions')
        .select('created_at, credits_amount, reference_id, reference_type')
        .eq('user_id', user.id)
        .eq('transaction_type', 'call_usage')
        .neq('credits_amount', 0) // Filter out zero-amount transactions
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });

      console.log('📊 Transaction Query Result:', {
        transactions,
        error,
        transactionCount: transactions?.length || 0,
        dateRange: `${startDate.toISOString()} to ${endDate.toISOString()}`
      });

      if (error) {
        console.error('❌ Transaction Query Error:', error);
        throw error;
      }

      // Group by day and aggregate credits
      // Since the new system can create multiple transactions per call, we need to aggregate properly
      const dailyMap = new Map<string, number>();
      
      transactions?.forEach(t => {
        // Parse the UTC timestamp - JavaScript automatically converts to local timezone
        const transactionDate = new Date(t.created_at);
        // Format as local date (this should respect the user's timezone)
        const date = format(transactionDate, 'yyyy-MM-dd');
        
        // Debug logging to help identify timezone issues
        if (process.env.NODE_ENV === 'development') {
          console.log('🕐 Transaction date debug:', {
            created_at: t.created_at,
            transactionDate: transactionDate.toString(),
            localDateString: date,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timezoneOffset: transactionDate.getTimezoneOffset()
          });
        }
        
        const currentCredits = dailyMap.get(date) || 0;
        // Use absolute value since call_usage transactions are negative
        dailyMap.set(date, currentCredits + Math.abs(t.credits_amount));
      });

      console.log('📈 Daily Map:', Object.fromEntries(dailyMap));

      // Fill in all days and calculate cumulative
      const days: DailyUsage[] = [];
      let cumulativeTotal = 0;
      
      // Create a proper date range loop that respects local timezone
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const dayCredits = dailyMap.get(dateStr) || 0;
        cumulativeTotal += dayCredits;
        
        days.push({
          date: dateStr,
          credits: dayCredits,
          cumulativeCredits: cumulativeTotal,
        });
        
        // Move to next day using setDate to avoid timezone issues
        currentDate.setDate(currentDate.getDate() + 1);
      }

      console.log('📅 Final Days Data:', days.slice(0, 5), '...', days.slice(-5));

      return days;
    },
    enabled: !!billingUsage,
  });

  // Calculate usage statistics
  const { data: usageStats } = useQuery({
    queryKey: ['usage-stats', billingUsage?.billingPeriodStart],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date();
      const yesterday = subDays(today, 1);
      const weekAgo = subDays(today, 7);

      console.log('📈 Usage Stats Debug:', {
        today: today.toISOString(),
        yesterday: yesterday.toISOString(),
        weekAgo: weekAgo.toISOString()
      });
      
      // Get usage for different periods
      // Updated to filter out zero-amount transactions for consistency
      const [todayUsage, yesterdayUsage, weekUsage] = await Promise.all([
        // Today's usage
        supabase
          .from('credit_transactions')
          .select('credits_amount')
          .eq('user_id', user.id)
          .eq('transaction_type', 'call_usage')
          .neq('credits_amount', 0) // Filter out zero-amount transactions
          .gte('created_at', startOfDay(today).toISOString())
          .lte('created_at', today.toISOString()),
        
        // Yesterday's usage
        supabase
          .from('credit_transactions')
          .select('credits_amount')
          .eq('user_id', user.id)
          .eq('transaction_type', 'call_usage')
          .neq('credits_amount', 0) // Filter out zero-amount transactions
          .gte('created_at', startOfDay(yesterday).toISOString())
          .lt('created_at', startOfDay(today).toISOString()),
        
        // Last 7 days usage
        supabase
          .from('credit_transactions')
          .select('credits_amount')
          .eq('user_id', user.id)
          .eq('transaction_type', 'call_usage')
          .neq('credits_amount', 0) // Filter out zero-amount transactions
          .gte('created_at', weekAgo.toISOString())
          .lte('created_at', today.toISOString()),
      ]);

      console.log('📊 Usage Stats Queries:', {
        todayCount: todayUsage.data?.length || 0,
        yesterdayCount: yesterdayUsage.data?.length || 0,
        weekCount: weekUsage.data?.length || 0,
        todayError: todayUsage.error,
        yesterdayError: yesterdayUsage.error,
        weekError: weekUsage.error
      });

      const todayTotal = todayUsage.data?.reduce((sum, t) => sum + Math.abs(t.credits_amount), 0) || 0;
      const yesterdayTotal = yesterdayUsage.data?.reduce((sum, t) => sum + Math.abs(t.credits_amount), 0) || 0;
      const weekTotal = weekUsage.data?.reduce((sum, t) => sum + Math.abs(t.credits_amount), 0) || 0;
      const weekAverage = weekTotal / 7;

      const stats = {
        today: todayTotal,
        yesterday: yesterdayTotal,
        weekAverage,
        trend: todayTotal > yesterdayTotal ? 'up' : todayTotal < yesterdayTotal ? 'down' : 'same',
      };

      console.log('📋 Final Usage Stats:', stats);

      return stats;
    },
    enabled: !!billingUsage,
  });

  if (isLoadingUsage || isLoadingDaily) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Usage Analytics</CardTitle>
          <CardDescription>Loading usage data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Calculate individual source usage
  const subscriptionUsagePercentage = billingUsage?.subscriptionCredits?.allocated && billingUsage.subscriptionCredits.allocated > 0
    ? (billingUsage.subscriptionCredits.used / billingUsage.subscriptionCredits.allocated) * 100
    : 0;

  const boostPackUsagePercentage = billingUsage?.boostPackCredits?.purchased && billingUsage.boostPackCredits.purchased > 0
    ? (billingUsage.boostPackCredits.used / billingUsage.boostPackCredits.purchased) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Credit Sources */}
      <Card>
        <CardHeader>
          <CardTitle>Credit Sources</CardTitle>
          <CardDescription>
            Breakdown of credit usage by source
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Subscription Credits - Only show if user has subscription credits */}
          {billingUsage?.subscriptionCredits && billingUsage.subscriptionCredits.allocated > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subscription Credits</span>
                <span className="font-medium">
                  {billingUsage.subscriptionCredits.used.toFixed(1)} / {billingUsage.subscriptionCredits.allocated.toFixed(1)}
                </span>
              </div>
              <Progress value={subscriptionUsagePercentage} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {subscriptionUsagePercentage.toFixed(1)}% used this period
              </p>
            </div>
          )}

          {/* Boost Pack Credits */}
          {billingUsage && billingUsage.boostPackCredits.purchased > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Boost Pack Credits</span>
                <span className="font-medium">
                  {billingUsage.boostPackCredits.used.toFixed(1)} / {billingUsage.boostPackCredits.purchased.toFixed(1)}
                </span>
              </div>
              <Progress value={boostPackUsagePercentage} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {boostPackUsagePercentage.toFixed(1)}% used
              </p>
            </div>
          )}

          {/* No credits message */}
          {(!billingUsage || 
            ((!billingUsage.subscriptionCredits || billingUsage.subscriptionCredits.allocated === 0) && 
             billingUsage.boostPackCredits.purchased === 0)) && (
            <p className="text-sm text-muted-foreground">
              No credits available. Purchase a boost pack to get started.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Usage Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Trend</CardTitle>
          <CardDescription>
            Daily credit consumption over time (shows at least 14 days for trend analysis)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyUsage}>
                <defs>
                  <linearGradient id="creditGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => format(new Date(date), 'MMM d')}
                  className="text-xs"
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  labelFormatter={(date) => format(new Date(date), 'MMM d, yyyy')}
                  formatter={(value: number) => [`${value.toFixed(1)} credits`, 'Total Used']}
                />
                <Area 
                  type="monotone" 
                  dataKey="cumulativeCredits" 
                  stroke="#3b82f6" 
                  fillOpacity={1}
                  fill="url(#creditGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Today</p>
              <p className="text-lg font-semibold">{usageStats?.today.toFixed(1) || '0'}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Yesterday</p>
              <p className="text-lg font-semibold">{usageStats?.yesterday.toFixed(1) || '0'}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">7-day avg</p>
              <p className="text-lg font-semibold">{usageStats?.weekAverage.toFixed(1) || '0'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Understanding Your Credits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground space-y-2">
            <p>• Your total credits include monthly allocation + any boost packs purchased</p>
            <p>• Credits are consumed based on call duration: 1.0 for calls &gt;15 seconds, 0.5 for voicemails, 0.1 for everything else</p>
            <p>• Unused monthly credits don't roll over, but boost pack credits do</p>
            <p>• Enable auto-refill to never run out of credits</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 